import { DefaultParams, newQueryKeys } from '@/queries/queryKeys';
import { DefaultEntity } from '@/types';
import { PAGE_DEFAULT } from '@amili-v3/amili-v3-design';
import { QueryFunctionContext } from '@tanstack/react-query';
import APIService from './axios.service';
import { CustomResponse } from './type';

export class BaseService<
  QueryKey extends keyof typeof newQueryKeys,
  Entity extends DefaultEntity,
  Create extends object = Partial<Entity>,
> {
  constructor(private _path: string) {}

  get path() {
    return this._path;
  }

  private getService = (
    params: DefaultParams,
  ): Promise<CustomResponse<Entity[]>> => {
    return APIService.get(this.path, { params });
  };

  get = (
    context: QueryFunctionContext<
      ReturnType<(typeof newQueryKeys)[QueryKey]['list']>
    >,
  ) => {
    const { params } = context.queryKey[0];
    return this.getService(params as DefaultParams);
  };

  getInfinite = (
    context: QueryFunctionContext<
      ReturnType<(typeof newQueryKeys)[QueryKey]['list']>
    >,
  ) => {
    const { pageParam = PAGE_DEFAULT, queryKey } = context;

    const params = {
      ...(queryKey?.[0]?.params as DefaultParams),
      page: pageParam as number,
    };

    return this.getService(params as DefaultParams);
  };

  getById = (
    context: QueryFunctionContext<
      ReturnType<(typeof newQueryKeys)[QueryKey]['detail']>
    >,
  ): Promise<Entity> => {
    const { id, channelId } = context.queryKey[0] as {
      id: string;
      channelId?: string;
    };
    return APIService.get(this.path + '/' + id, {
      params: {
        channelId,
      },
    });
  };

  create = (payload: Create): Promise<Entity> => {
    return APIService.post(this.path, payload);
  };

  update = (payload: Partial<Entity>): Promise<Entity> => {
    const { id } = payload;
    return APIService.put(this.path + '/' + id, payload);
  };

  updateByPatch = (payload: Partial<Entity>): Promise<Entity> => {
    const { id, ...rest } = payload;
    return APIService.patch(this.path + '/' + id, rest);
  };

  massUpdate = (payload: {
    condition: Partial<Entity>;
    payload: Partial<Entity>;
  }) => {
    return APIService.patch(`${this.path}/mass-update`, payload);
  };

  delete = (id: string): Promise<unknown> => {
    return APIService.delete(this.path + '/' + id);
  };
}
